import { Button, Icon } from '@pwc/pwc-deals-ui';
import classNames from 'classnames';
import PropType from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import styles from './Section.module.scss';
import CONSTANTS from '../../../../../features/analyses/Freeform/FreeformAnalysisLayout/AddReportDataLayoutContainer/Constants';
import LayoutIcon from '../../../../../features/analyses/Freeform/FreeformAnalysisLayout/AddReportDataLayoutContainer/LayoutIcon';
import SectionControls from './SectionControls';
import useTabletOrMobile from '../../../../hooks/useTabletOrMobile';
import {
  getReportFormatLayoutState,
  setActiveSectionId,
  setFloatingTabsHeight,
  setFloatingTabsTop,
} from '../../../../redux/reportFormat/reportFormatSlice';
import { useSections } from '../../SectionContext';
import { setSectionToScroll } from '../../SectionContextUtils';
import SectionArea from './SectionArea/SectionArea';

function Section({
  sectionData,
  libraryItemMap,
  droppableLocations,
  onAddSection,
  onRemoveSection,
  onMoveSection,
  lastSection,
  controlsActive,
  targetDroppableLocationIds,
  showRightPanel,
  // activeSection = false, // TODO: will use for styling
  scrollIntoView = true,
  moreSectionsAllowed = false,
  loading = false,
}) {
  const ref = useRef();
  const [isActive, setIsActive] = useState(false);
  const [{ sectionIdToScroll }, useSectionsDispatch] = useSections();
  const isTabletOrMobile = useTabletOrMobile();

  const dispatch = useDispatch();
  const sectionId = sectionData.order - 1;

  const { activeSectionIndex, floatingTabsHeight } = useSelector(
    getReportFormatLayoutState
  );

  const currentHeight = ref.current ? ref.current.offsetHeight : 0;
  const currentTop = ref.current ? ref.current.offsetTop : 0;

  useEffect(() => {
    if (sectionIdToScroll === sectionId && currentHeight) {
      if (scrollIntoView) {
        ref.current.scrollIntoView({
          behavior: 'smooth',
        });
      }
      setSectionToScroll(null, useSectionsDispatch);
    }
  }, [
    currentHeight,
    useSectionsDispatch,
    scrollIntoView,
    sectionId,
    sectionIdToScroll,
  ]);

  const handleOnSectionClick = () => {
    /*
     * Bug: PWC-5795
     * If section clicked, floatingTabs needs to be positioned to that section
     */
    if (!isTabletOrMobile && activeSectionIndex !== sectionId) {
      dispatch(setActiveSectionId({ index: sectionId }));
      dispatch(
        setFloatingTabsTop({
          top: currentTop,
        })
      );
      if (currentHeight !== floatingTabsHeight) {
        dispatch(
          setFloatingTabsHeight({
            height: currentHeight,
          })
        );
      }
    }
  };

  useEffect(() => {
    if (activeSectionIndex === sectionId) {
      if (!isActive) {
        dispatch(
          setFloatingTabsTop({
            top: currentTop,
          })
        );
        dispatch(
          setFloatingTabsHeight({
            height: currentHeight,
          })
        );
      }

      setIsActive(true);
    } else if (isActive) {
      setIsActive(false);
    }
  }, [
    isActive,
    activeSectionIndex,
    sectionId,
    dispatch,
    currentTop,
    currentHeight,
  ]);

  const handleRemoveSection = () => {
    const newSectionIndex = Math.max(0, sectionData.order - 1 - 1);
    dispatch(setActiveSectionId({ index: newSectionIndex }));

    onRemoveSection(sectionData.order);
  };

  const handleMoveSection = (direction) => {
    onMoveSection(direction, sectionData);
  };

  return (
    // eslint-disable-next-line
    <div
      ref={ref}
      data-section-id={sectionId}
      key={`Section - ${sectionData.order}`}
      className={styles.section}
      data-testid="section"
      onClick={handleOnSectionClick}
    >
      <div className={styles['section__top-row']} data-testid="section-top-row">
        <div className={styles['section__top-row__row-1_2']} />
        <div className={styles['section__top-row__row-2_2']}>
          <SectionControls
            section={sectionData}
            lastSection={lastSection}
            controlsActive={controlsActive}
            handleMoveSection={handleMoveSection}
            handleRemoveSection={handleRemoveSection}
          />
        </div>
      </div>
      <div
        className={classNames(styles['section__middle-row'], {
          [CONSTANTS.LAYOUT_TYPE_TO_SECTION_CLASSNAME_MAP[
            sectionData.layoutType
          ]]: sectionData.layoutType,
        })}
        data-testid="section-middle-row"
      >
        {targetDroppableLocationIds &&
          targetDroppableLocationIds.map((droppableLocationId, panelNumber) => {
            const droppableLocation = droppableLocations[droppableLocationId];
            const libraryItems =
              droppableLocation &&
              droppableLocation.libraryItemIds
                .map((id) => libraryItemMap[id])
                .filter((libraryItem) => !!libraryItem);
            return (
              <React.Fragment key={droppableLocationId}>
                {droppableLocation && (
                  <>
                    {isTabletOrMobile && (
                      <LayoutIcon
                        name={
                          CONSTANTS.LAYOUT_TYPE_TO_ICON_MAP[
                            `${sectionData.layoutType}-${droppableLocation.position}`
                          ]
                        }
                      />
                    )}
                    <SectionArea
                      panelNumber={panelNumber}
                      key={droppableLocationId}
                      areaClass={droppableLocationId}
                      droppableLocation={
                        droppableLocations[droppableLocationId]
                      }
                      libraryItems={libraryItems}
                      libraryItemMap={libraryItemMap}
                      droppableLocations={droppableLocations}
                      sectionData={sectionData}
                      showRightPanel={showRightPanel}
                      loading={loading}
                    />
                  </>
                )}
              </React.Fragment>
            );
          })}
      </div>
      {lastSection && moreSectionsAllowed && (
        <div
          className={styles['section__bottom-row']}
          data-testid="section-bottom-row"
        >
          <hr className={styles['section-line']} />
          <Button className="button-no-underline" onClick={onAddSection}>
            <Icon
              name="add"
              width={24}
              height={24}
              className="fill icon-orange-theme"
            />
            <span className={styles['section__bottom-row__button-text']}>
              Add new section
            </span>
          </Button>
          <hr className={styles['section-line']} />
        </div>
      )}
    </div>
  );
}

SectionArea.propTypes = {
  sectionData: PropType.shape({
    order: PropType.number,
    layoutType: PropType.number,
    items: PropType.arrayOf(PropType.any),
  }),
  onAddSection: PropType.func,
  onRemoveSection: PropType.func,
  targetDroppableLocationIds: PropType.arrayOf(PropType.string),
  lastSection: PropType.bool,
  activeSection: PropType.bool,
};

export default Section;
